import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseApi, RequestType } from '@kapi';
import { EnvironmentVariablesService } from '@kenv';
import { EligibilityCandidate } from '@kp/eligibility/eligibility-candidate.model';
import { EligibilityVerification } from '@kp/eligibility/eligibility-verification.model';
import { DataStoreService } from '@kservice';
import { DateTimeUtil } from '@kutil';
import { firstValueFrom, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

interface EligibilityCandidateResponse {
  candidate: EligibilityCandidate;
  verificationLink: string;
}

export interface AarpMembershipVerificationResponse {
  aarpStatus: number;
  aarpStatusLabel: string;
  nextAction: 'join_aarp' | 'renew_membership' | null;
  joinOrRenewUrl?: string | null;
  commitToken?: string;
  commitTokenExpiresAt?: string;
  groupChanged?: boolean;
  verifiedAt?: string;
}

export interface AarpMembershipVerificationErrorResponse {
  message: string;
  retryAfterSeconds?: number;
}

export type AarpMembershipVerificationResult =
  | { ok: true; data: AarpMembershipVerificationResponse }
  | { ok: false; error: AarpMembershipVerificationErrorResponse; statusCode: number };

type AarpMembershipVerificationHttpBody = AarpMembershipVerificationResponse | AarpMembershipVerificationErrorResponse;

const DEFAULT_AARP_ERROR_RESPONSE: AarpMembershipVerificationErrorResponse = {
  message: 'AARP_SERVICE_UNAVAILABLE',
};

function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function toAarpMembershipVerificationErrorResponse(value: unknown): AarpMembershipVerificationErrorResponse {
  if (!isObjectRecord(value)) {
    return DEFAULT_AARP_ERROR_RESPONSE;
  }

  const message = value.message;
  if (typeof message !== 'string' || message.length === 0) {
    return DEFAULT_AARP_ERROR_RESPONSE;
  }

  const retryAfterSeconds = value.retryAfterSeconds;
  if (typeof retryAfterSeconds === 'number') {
    return { message, retryAfterSeconds };
  }

  return { message };
}

function isAarpMembershipVerificationResponse(value: unknown): value is AarpMembershipVerificationResponse {
  if (!isObjectRecord(value)) {
    return false;
  }

  const aarpStatus = value.aarpStatus;
  const aarpStatusLabel = value.aarpStatusLabel;
  const nextAction = value.nextAction;

  return (
    typeof aarpStatus === 'number' &&
    typeof aarpStatusLabel === 'string' &&
    (nextAction === null || typeof nextAction === 'string')
  );
}

@Injectable({
  providedIn: 'root',
})
export class EligibilityVerificationApi extends BaseApi {
  constructor(
    private client: HttpClient,
    dataStoreService: DataStoreService,
    private _environmentVariablesService: EnvironmentVariablesService
  ) {
    super(client, dataStoreService, _environmentVariablesService);
  }
  async verifyEligibility(verificationObject: EligibilityVerification): Promise<EligibilityCandidate> {
    const url = this.buildUrl('/eligibility', true);
    const request$ = this.performRequest<EligibilityCandidateResponse>(RequestType.POST, url, {
      requestBody: {
        verificationObject: verificationObject.serialize(),
        deviceCreatedTimestamp: DateTimeUtil.formatInLocal(),
      },
    }).pipe(
      catchError((error) => {
        console.warn('Failed verifying eligibility: ', error);
        return of(null);
      })
    );
    const response = await firstValueFrom<HttpResponse<EligibilityCandidateResponse>>(request$).catch((error): null => {
      console.warn('Error verifying eligibility: ', error);
      return null;
    });
    if (response?.ok) {
      //TODO: update API to return link as part of candidate object
      const candidate = new EligibilityCandidate().deserialize(response.body?.candidate);
      candidate.eligibilityLink = response.body?.verificationLink;
      return candidate;
    }
    return null;
  }

  async verifyAarpMembership(identifier: string): Promise<AarpMembershipVerificationResult> {
    const url = this.buildUrl('/aarp-membership/verify', true);
    const request$ = this.performRequest<AarpMembershipVerificationResponse>(RequestType.POST, url, {
      includeToken: true,
      requestBody: {
        identifier: identifier.trim(),
      },
    }).pipe(
      catchError((error: unknown) => {
        const statusCode = error instanceof HttpErrorResponse && error.status > 0 ? error.status : 500;
        const errorBody =
          error instanceof HttpErrorResponse
            ? toAarpMembershipVerificationErrorResponse(error.error)
            : DEFAULT_AARP_ERROR_RESPONSE;
        return of(
          new HttpResponse<AarpMembershipVerificationHttpBody>({
            status: statusCode,
            body: errorBody,
          })
        );
      })
    );

    const response = await firstValueFrom<HttpResponse<AarpMembershipVerificationHttpBody>>(request$).catch(
      (error): null => {
        console.warn('Error verifying AARP membership: ', error);
        return null;
      }
    );

    if (response?.ok && isAarpMembershipVerificationResponse(response.body)) {
      return {
        ok: true,
        data: response.body,
      };
    }

    return {
      ok: false,
      statusCode: response?.status || 500,
      error: toAarpMembershipVerificationErrorResponse(response?.body),
    };
  }

  async checkAarpMembership(identifier: string): Promise<AarpMembershipVerificationResult> {
    const url = this.buildUrl('/aarp-membership/check', true);
    const request$ = this.performRequest<AarpMembershipVerificationResponse>(RequestType.POST, url, {
      includeToken: true,
      requestBody: {
        identifier: identifier?.trim(),
      },
    }).pipe(
      catchError((error: unknown) => {
        const statusCode = error instanceof HttpErrorResponse && error.status > 0 ? error.status : 500;
        const errorBody =
          error instanceof HttpErrorResponse
            ? toAarpMembershipVerificationErrorResponse(error.error)
            : DEFAULT_AARP_ERROR_RESPONSE;
        return of(
          new HttpResponse<AarpMembershipVerificationHttpBody>({
            status: statusCode,
            body: errorBody,
          })
        );
      })
    );
    const response = await firstValueFrom<HttpResponse<AarpMembershipVerificationHttpBody>>(request$).catch(
      (error): null => {
        console.warn('Error checking AARP membership: ', error);
        return null;
      }
    );

    if (response?.ok && isAarpMembershipVerificationResponse(response.body)) {
      return {
        ok: true,
        data: response.body,
      };
    }

    return {
      ok: false,
      statusCode: response?.status || 500,
      error: toAarpMembershipVerificationErrorResponse(response?.body),
    };
  }

  async applyAarpMembership(commitToken: string): Promise<AarpMembershipVerificationResponse | null> {
    const url = this.buildUrl('/aarp-membership/apply', true);
    const request$ = this.performRequest<AarpMembershipVerificationResponse>(RequestType.POST, url, {
      includeToken: true,
      requestBody: {
        commitToken: commitToken?.trim(),
      },
    }).pipe(
      catchError((error) => {
        console.warn('Failed applying AARP membership: ', error);
        return of(null);
      })
    );
    const response = await firstValueFrom<HttpResponse<AarpMembershipVerificationResponse>>(request$).catch(
      (error): null => {
        console.warn('Error applying AARP membership: ', error);
        return null;
      }
    );
    if (response?.ok) {
      return response.body;
    }
    return null;
  }
}
